import React from "react";
import { Link } from "react-router-dom";
import { OrIcon } from "../../atoms/or-icon";
import "./sidebar-business.css";
import { SidebarBusinessProps, useSidebarBusiness } from "./sidebar-business.hook";

export type { SidebarBusinessChildItem, SidebarBusinessItem, SidebarBusinessProps } from "./sidebar-business.hook";

function LockIcon(): React.ReactElement {
  return (
    <svg aria-hidden="true" fill="none" height="14" viewBox="0 0 24 24" width="14">
      <path
        d="M7 10V7a5 5 0 1 1 10 0v3m-9 0h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function BlockedIndicator(): React.ReactElement {
  return (
    <span className="sidebar-business__blocked-indicator" title="Bloqueado">
      <LockIcon />
    </span>
  );
}

export function SidebarBusiness({
  ariaLabel = "Navegación principal",
  collapseLabel = "Colapsar menú",
  items,
  logo,
  ...rest
}: SidebarBusinessProps): React.ReactElement | null {
  const {
    collapseSidebar,
    isCollapsedResolved,
    isItemExpanded,
    isMobileOpenResolved,
    isSectionActive,
    selectItem,
    setMobileOpen,
    toggleItem,
    wrapperClassName,
  } = useSidebarBusiness({
    ariaLabel,
    collapseLabel,
    items,
    logo,
    ...rest,
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={wrapperClassName}>
      <button
        aria-label="Cerrar menú"
        className={`sidebar-business__overlay ${isMobileOpenResolved ? "sidebar-business__overlay--visible" : ""}`}
        onClick={() => setMobileOpen(false)}
        type="button"
      />
      <aside className="sidebar-business__panel">
        <div className="sidebar-business__header">
          <div className="sidebar-business__logo-wrapper">
            {logo ?? <span className="sidebar-business__logo-fallback">OpenReferences</span>}
          </div>
          <button
            aria-label={collapseLabel}
            className="sidebar-business__collapse-button"
            onClick={collapseSidebar}
            type="button"
          >
            <OrIcon name="sidebarCollapse" size={24} />
          </button>
        </div>

        <nav aria-label={ariaLabel} className="sidebar-business__nav">
          <ul className="sidebar-business__list">
            {items.map((item) => {
              const isExpanded = isItemExpanded(item.id);
              const isLeaf = !item.children?.length;
              const isActive = isLeaf ? isSectionActive(item) : Boolean(item.isActive);
              const children = item.children ?? [];

              if (isLeaf) {
                return (
                  <li className="sidebar-business__item" key={item.id}>
                    {item.to ? (
                      <Link
                        aria-current={isActive ? "page" : undefined}
                        className={`sidebar-business__action ${isActive ? "sidebar-business__action--active" : ""}`}
                        onClick={() => selectItem(item)}
                        to={item.to}
                      >
                        {item.icon ? <span className="sidebar-business__icon">{item.icon}</span> : null}
                        <span className="sidebar-business__label">{item.label}</span>
                        {item.isBlocked ? <BlockedIndicator /> : null}
                      </Link>
                    ) : (
                      <button
                        className={`sidebar-business__action ${isActive ? "sidebar-business__action--active" : ""}`}
                        onClick={() => toggleItem(item)}
                        type="button"
                      >
                        {item.icon ? <span className="sidebar-business__icon">{item.icon}</span> : null}
                        <span className="sidebar-business__label">{item.label}</span>
                        {item.isBlocked ? <BlockedIndicator /> : null}
                      </button>
                    )}
                  </li>
                );
              }

              return (
                <li
                  className={`sidebar-business__item ${isExpanded ? "sidebar-business__item--expanded" : ""}`}
                  key={item.id}
                >
                  <button
                    aria-controls={`sidebar-business-submenu-${item.id}`}
                    aria-expanded={isExpanded}
                    className={`sidebar-business__action ${
                      isActive ? "sidebar-business__action--active" : ""
                    } sidebar-business__action--accordion`}
                    onClick={() => toggleItem(item)}
                    type="button"
                    >
                      {item.icon ? <span className="sidebar-business__icon">{item.icon}</span> : null}
                      <span className="sidebar-business__label">{item.label}</span>
                      {item.isBlocked ? <BlockedIndicator /> : null}
                      <span className="sidebar-business__arrow">
                        <OrIcon name="chevronDown" size={16} />
                      </span>
                  </button>
                  <div
                    className={`sidebar-business__submenu ${isExpanded ? "sidebar-business__submenu--expanded" : ""}`}
                    id={`sidebar-business-submenu-${item.id}`}
                  >
                    <ul className="sidebar-business__submenu-list">
                      {children.map((child) => (
                        <li className="sidebar-business__submenu-item" key={child.id}>
                          {child.to ? (
                            <Link
                              aria-current={child.isActive ? "page" : undefined}
                              className={`sidebar-business__submenu-action ${
                                child.isActive ? "sidebar-business__submenu-action--active" : ""
                              }`}
                              onClick={() => selectItem(child)}
                              to={child.to}
                            >
                              {child.label}
                              {child.isBlocked ? <BlockedIndicator /> : null}
                            </Link>
                          ) : (
                            <button
                              className={`sidebar-business__submenu-action ${
                                child.isActive ? "sidebar-business__submenu-action--active" : ""
                              }`}
                              onClick={() => selectItem(child)}
                              type="button"
                            >
                              {child.label}
                              {child.isBlocked ? <BlockedIndicator /> : null}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </div>
  );
}
